import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Select } from "@/components/ui/Select";
import { ALL_ELEMENTS } from "@/data";
import { useForceStore } from "@/store/forceStore";
import { useUiStore } from "@/store/uiStore";
import type { ForceElement } from "@/types/force";
import type { TrainingTier } from "@/types/game-data";
import { calculateElementCost } from "@/utils/points";
import { ModelEditor } from "./ModelEditor";

interface ElementCardProps {
	element: ForceElement;
}

const TIER_LABELS: Record<string, string> = {
	tier1: "Tier 1",
	tier2: "Tier 2",
	veteran: "Veteran",
	enlisted: "Enlisted",
	militia: "Militia",
	conscript: "Conscript",
	criminal: "Criminal",
};

const TIER_BADGE_VARIANTS: Record<string, "amber" | "blue" | "purple" | "gray" | "red"> = {
	tier1: "amber",
	tier2: "blue",
	veteran: "purple",
	enlisted: "gray",
	militia: "amber",
	conscript: "gray",
	criminal: "red",
};

export function ElementCard({ element }: ElementCardProps) {
	const removeElement = useForceStore((s) => s.removeElement);
	const setElementTier = useForceStore((s) => s.setElementTier);
	const addModel = useForceStore((s) => s.addModel);

	const expanded = useUiStore((s) => s.expandedElementId === element.id);
	const toggleExpanded = useUiStore((s) => s.toggleExpandedElement);

	const template = ALL_ELEMENTS.find((t) => t.id === element.templateId);
	const cost = calculateElementCost(element);
	const canAddModel = template && element.models.length < template.maxModels;

	const tierOptions = template
		? template.allowedTiers.map((t) => ({
				value: t,
				label: TIER_LABELS[t] ?? t,
			}))
		: [];

	// Show tier selector when there are multiple tier options
	const showTierSelector = tierOptions.length > 1;

	return (
		<Card variant="outlined" className="space-y-3">
			{/* Element header — responsive: wrap badges on mobile */}
			<div className="flex items-start justify-between gap-2">
				<button
					type="button"
					className="flex items-center gap-2 cursor-pointer flex-wrap min-h-[44px] lg:min-h-0"
					onClick={() => toggleExpanded(element.id)}
				>
					<span className="text-text-muted text-sm">{expanded ? "▾" : "▸"}</span>
					<h3 className="font-semibold text-text-primary">{template?.name ?? "Unknown Element"}</h3>
					<Badge variant={TIER_BADGE_VARIANTS[element.tier] ?? "gray"} size="sm">
						{TIER_LABELS[element.tier] ?? element.tier}
					</Badge>
					<Badge variant="default" size="sm">
						{element.models.length} model
						{element.models.length !== 1 ? "s" : ""}
					</Badge>
				</button>
				<div className="flex items-center gap-2 lg:gap-3 shrink-0">
					<span className="font-mono text-sm font-semibold text-accent-secondary">{cost}r</span>
					<Button variant="danger" size="sm" onClick={() => removeElement(element.id)}>
						Remove
					</Button>
				</div>
			</div>

			{/* Included gear info for base-cost elements */}
			{expanded && template?.includedGear && template.includedGear.length > 0 && (
				<div className="text-xs text-text-muted bg-bg-tertiary/50 px-2 py-1.5 rounded">
					Included: {template.includedGear.join(", ")}
					{template.baseCost != null && ` (in ${template.baseCost}r base)`}
				</div>
			)}

			{/* Expanded content */}
			{expanded && (
				<div className="space-y-3 pt-2 border-t border-border-default">
					{/* Tier selector — shown for any element with multiple tier options */}
					{showTierSelector && (
						<Select
							label="Training Tier"
							value={element.tier}
							onChange={(v) => setElementTier(element.id, v as TrainingTier)}
							options={tierOptions}
							className="max-w-full lg:max-w-48"
						/>
					)}

					{/* Models */}
					<div className="space-y-2">
						{element.models.map((model) => (
							<ModelEditor key={model.id} element={element} model={model} />
						))}
					</div>

					{/* Add model button */}
					{canAddModel && (
						<Button variant="secondary" size="sm" onClick={() => addModel(element.id)}>
							+ Add Soldier ({element.models.length}/{template?.maxModels})
							{template?.additionalModelCost
								? ` · ${template.additionalModelCost}r`
								: ""}
						</Button>
					)}
				</div>
			)}
		</Card>
	);
}
